import React, { useEffect, useState } from 'react';
import { MdSearch, MdPublic, MdAdd, MdEdit, MdDelete, MdToggleOn, MdToggleOff } from 'react-icons/md';
import { locationApi } from '../../../api/adminapi';
import { toast } from 'react-toastify';
import '../Admin.css';
import './LocationMaster.css';
import './BookRoom.css';

function LocationMaster() {
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    locationCode: '',
    locationName: '',
    region: '',
    city: '',
    state: '',
    timezone: 'Asia/Kolkata',
    address: '',
    displayOrder: 0,
    status: 'ACTIVE'
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await locationApi.getAllLocations();
      const locationsData = response?.data || response || [];
      setLocations(locationsData);
    } catch (err) {
      console.error('Error fetching locations:', err);
      if (err.response?.status === 500 || err.response?.status === 403) {
        setLocations([]);
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to fetch locations');
        toast.error('Failed to load locations');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddClick = () => {
    setEditingLocation(null);
    setFormData({
      locationCode: '',
      locationName: '',
      region: '',
      city: '',
      state: '',
      timezone: 'Asia/Kolkata',
      address: '',
      displayOrder: 0,
      status: 'ACTIVE'
    });
    setShowModal(true);
  };

  const handleEditClick = (location) => {
    setEditingLocation(location);
    setFormData({
      locationCode: location.locationCode,
      locationName: location.locationName,
      region: location.region || '',
      city: location.city || '',
      state: location.state || '',
      timezone: location.timezone || 'Asia/Kolkata',
      address: location.address || '',
      displayOrder: location.displayOrder || 0,
      status: location.status || 'ACTIVE'
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLocation(null);
    setFormData({
      locationCode: '',
      locationName: '',
      region: '',
      city: '',
      state: '',
      timezone: 'Asia/Kolkata',
      address: '',
      displayOrder: 0,
      status: 'ACTIVE'
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.locationCode.trim() || !formData.locationName.trim()) {
      toast.error('Location code and name are required');
      return;
    }

    try {
      setIsLoading(true);

      if (editingLocation) {
        await locationApi.updateLocation(editingLocation.id, formData);
        toast.success('Location updated successfully');
      } else {
        await locationApi.createLocation(formData);
        toast.success('Location created successfully');
      }

      handleCloseModal();
      await fetchLocations();
    } catch (err) {
      console.error('Error saving location:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to save location';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusToggle = async (location) => {
    try {
      setIsLoading(true);
      const newStatus = location.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      await locationApi.changeLocationStatus(location.id, newStatus);
      toast.success(`Location ${newStatus === 'ACTIVE' ? 'activated' : 'deactivated'} successfully`);
      await fetchLocations();
    } catch (err) {
      console.error('Error changing status:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to change status';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (location) => {
    if (!window.confirm(`Are you sure you want to delete location: ${location.locationName}?`)) {
      return;
    }

    try {
      setIsLoading(true);
      await locationApi.deleteLocation(location.id);
      toast.success('Location deleted successfully');
      await fetchLocations();
    } catch (err) {
      console.error('Error deleting location:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to delete location';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter locations based on search query
  const filteredLocations = locations.filter(location => {
    const query = searchQuery.toLowerCase();
    return (
      location.locationCode?.toLowerCase().includes(query) ||
      location.locationName?.toLowerCase().includes(query) ||
      location.city?.toLowerCase().includes(query) ||
      location.state?.toLowerCase().includes(query) ||
      location.region?.toLowerCase().includes(query)
    );
  });

  if (error && locations.length === 0) {
    return (
      <div className="admin-view location-master-view">
        <div className="error-container">
          <p className="error-text">{error}</p>
          <button className="retry-button" onClick={fetchLocations}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-view location-master-view">
      {/* Combined Header and Filters Row */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdPublic className="title-icon" />
            Location Master
          </h2>
        </div>

        <div className="header-filters-compact">
          <div className="input search-compact">
            <span className="icon"><MdSearch /></span>
            <input
              placeholder="Search locations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <button
            className="btn-book-compact"
            onClick={handleAddClick}
            style={{ marginLeft: '0.5rem' }}
          >
            <MdAdd />
            <span>Add Location</span>
          </button>
        </div>
      </div>

      {isLoading && locations.length === 0 ? (
        <div className="loading-state">
          <div className="loading-spinner-large"></div>
          <p>Loading locations...</p>
        </div>
      ) : (
        <div className="locations-table-container">
          {filteredLocations.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🌍</div>
              <h3>No Locations Found</h3>
              <p>
                {searchQuery
                  ? 'No locations match your search criteria'
                  : 'There are currently no locations in the system'}
              </p>
              {!searchQuery && (
                <button className="btn-book-compact" onClick={handleAddClick}>
                  <MdAdd />
                  <span>Add First Location</span>
                </button>
              )}
            </div>
          ) : (
            <table className="locations-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Location Name</th>
                  <th>City</th>
                  <th>State</th>
                  <th>Region</th>
                  <th>Status</th>
                  <th>Display Order</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLocations.map(location => (
                  <tr key={location.id} className={location.status === 'INACTIVE' ? 'inactive-row' : ''}>
                    <td className="location-code">{location.locationCode}</td>
                    <td className="location-name">{location.locationName}</td>
                    <td>{location.city || '-'}</td>
                    <td>{location.state || '-'}</td>
                    <td>{location.region || '-'}</td>
                    <td>
                      <span className={`status-badge status-${location.status.toLowerCase()}`}>
                        {location.status}
                      </span>
                    </td>
                    <td className="display-order">{location.displayOrder}</td>
                    <td className="actions-cell">
                      <button
                        className="btn-action-compact btn-edit"
                        onClick={() => handleEditClick(location)}
                        title="Edit location"
                        disabled={isLoading}
                      >
                        <MdEdit />
                      </button>
                      <button
                        className={`btn-action-compact ${location.status === 'ACTIVE' ? 'btn-deactivate' : 'btn-activate'}`}
                        onClick={() => handleStatusToggle(location)}
                        title={location.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                        disabled={isLoading}
                      >
                        {location.status === 'ACTIVE' ? <MdToggleOff /> : <MdToggleOn />}
                      </button>
                      <button
                        className="btn-action-compact btn-delete"
                        onClick={() => handleDelete(location)}
                        title="Delete location"
                        disabled={isLoading}
                      >
                        <MdDelete />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingLocation ? 'Edit Location' : 'Add New Location'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="location-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="locationCode">
                    Location Code <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="locationCode"
                    name="locationCode"
                    value={formData.locationCode}
                    onChange={handleInputChange}
                    placeholder="e.g., BLR, NCR, HYD"
                    maxLength="50"
                    required
                    disabled={editingLocation !== null}
                  />
                  <small>Unique code for the location (cannot be changed after creation)</small>
                </div>

                <div className="form-group">
                  <label htmlFor="locationName">
                    Location Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="locationName"
                    name="locationName"
                    value={formData.locationName}
                    onChange={handleInputChange}
                    placeholder="e.g., Bangalore, NCR, Hyderabad"
                    maxLength="100"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">City</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="e.g., Bangalore"
                    maxLength="100"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="state">State</label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="e.g., Karnataka"
                    maxLength="100"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="region">Region</label>
                  <select
                    id="region"
                    name="region"
                    value={formData.region}
                    onChange={handleInputChange}
                  >
                    <option value="">Select region</option>
                    <option value="North">North</option>
                    <option value="South">South</option>
                    <option value="East">East</option>
                    <option value="West">West</option>
                    <option value="Central">Central</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="timezone">Timezone</label>
                  <select
                    id="timezone"
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleInputChange}
                  >
                    <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                    <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                    <option value="America/New_York">America/New_York (EST)</option>
                    <option value="Europe/London">Europe/London (GMT)</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="address">Address</label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Full address"
                  rows="3"
                  maxLength="500"
                />
              </div>

              <div className="form-group">
                <label htmlFor="displayOrder">Display Order</label>
                <input
                  type="number"
                  id="displayOrder"
                  name="displayOrder"
                  value={formData.displayOrder}
                  onChange={handleInputChange}
                  placeholder="0"
                  min="0"
                />
                <small>Lower numbers appear first in lists</small>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : editingLocation ? 'Update Location' : 'Add Location'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default LocationMaster;
